<script lang="ts" setup>
import { defineProps, onMounted, ref, computed, onBeforeUnmount } from 'vue'
import { getLanguage } from '@/helpers/language-helper'

const props = defineProps<{
  backgroundMedia?: string
  isVideo?: boolean
  title?: string
  content?: string
  logos?: string
  isUfc?: boolean
  logoTitle?: string
}>()

const lang = ref<string | null>('en')

onMounted(() => {
  lang.value = getLanguage()
})

const isJapaneseUfc = computed(() => lang.value === 'jp' && props.isUfc)

const screenWidth = ref(window.innerWidth)
const backgroundSizes = computed(() => {
  return screenWidth.value < 1024 ? '25%, 40%' : 'auto, auto'
})
const backgroundPositions = computed(() => {
  return screenWidth.value >= 1440 ? 'top left 60px, bottom right' : 'top left, bottom right'
})
const handleResize = () => {
  screenWidth.value = window.innerWidth
}

onMounted(async () => {
  window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
})
</script>

<template>
  <div
    class="flex flex-col items-center justify-center mx-auto p-2.5 sm:pt-20 sm:pb-10 md:pt-30 md:pb-15 md:px-10 mlg:px-20 xl:px-30 lg:pt-[200px] lg:pb-25 1380:pt-[350px] relative overflow-hidden"
  >
    <video
      v-if="isVideo"
      :src="backgroundMedia"
      autoplay
      loop
      muted
      playsinline
      class="absolute inset-0 object-cover w-full h-full"
    ></video>
    <div
      v-else
      :style="{
        backgroundImage: isUfc ? `url(${backgroundMedia})` : `linear-gradient(0deg, rgba(0, 0, 0, 0.76) 0%, rgba(0, 0, 0, 0.76) 100%), url(${backgroundMedia})`
      }"
      class="absolute inset-0 bg-top bg-cover"
    ></div>
    <div v-if="isVideo" class="absolute inset-0 bg-gradient-to-b from-black/75 to-black/75"></div>
    <div
      class="relative z-10 text-center flex flex-col items-center justify-center w-full mx-auto max-w-[1200px]"
      :style="{
        backgroundImage: `url('/assets/sponsorships/topleft.png'), url('/assets/sponsorships/bottomright.svg')`,
        backgroundPosition: `${backgroundPositions}`,
        backgroundRepeat: 'no-repeat, no-repeat',
        backgroundSize: `${backgroundSizes}`
      }"
    >
      <p
        class="text-hantec-primary uppercase sponsor-subtitle text-center max-w-[360px] sm:max-w-[442px] pb-7.5 pt-16 md:pt-20"
        :class="isUfc ? 'xl:pt-32' : ''"
      >
        {{ title }}
      </p>
      <div
        class="max-w-[360px] max-h-[300px] sm:max-w-[898px] sm:max-h-[270px] mb-25 sm:mb-45 md:mb-37 xl:mb-35"
        :class="isUfc ? 'xl:mb-60' : ''"
      >
        <div v-if="isJapaneseUfc" class="max-w-[300px] sm:max-w-[500px] md:max-w-[460px] lg:max-w-[500px] 1440:max-w-[898px] text-center text-white uppercase sponsor-title">
          <span class="whitespace-nowrap">最高の</span>
          <span class="whitespace-nowrap">パフォーマンスの</span>
          <span class="whitespace-nowrap">ために</span>
        </div>
        <p
          v-else
          class="max-w-[300px] sm:max-w-[500px] md:max-w-[460px] lg:max-w-[500px] 1440:max-w-[898px] text-center text-white uppercase sponsor-title"
        >
          {{ content }}
        </p>
      </div>
    </div>
    <div
      class="relative z-10 pt-5 xsm:pt-10 sm:pt-15 lg:pt-[95px] max-w-[200px] xsm:max-w-[250px] sm:max-w-[300px] md:max-w-[380px] lg:max-w-[400px] xl:max-w-none"
    >
      <img class="" :src="logos" />
    </div>
    <div v-if="isUfc" class="text-[8px] md:text-[11px] font-semibold uppercase text-white pt-2.5 z-10">{{ logoTitle }}</div>
  </div>
</template>

<style lang="sass" scoped>
.sponsor-subtitle
    @apply text-[10px] font-bold leading-normal tracking-[4px]

.sponsor-title
    @apply text-[30px] font-semibold leading-[104%]

@screen sm
    .sponsor-title
        @apply text-[60px] font-semibold leading-[104%]

    .sponsor-subtitle
        @apply text-[14px] font-bold leading-normal tracking-[4px]

@screen md
    .sponsor-title
        @apply text-[60px] font-semibold leading-[104%]
    .sponsor-subtitle
        @apply text-[16px] font-bold leading-normal tracking-[4px]

@screen lg
    .sponsor-title
        @apply text-[65px] font-semibold leading-[104%]
    .sponsor-subtitle
        @apply text-[20px] font-bold leading-normal tracking-[4px]
</style>
